import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Mail, Phone, Clock, Zap } from "lucide-react";

const DripSequences = () => {
  const [sequences, setSequences] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSeq, setEditingSeq] = useState(null);
  const [formData, setFormData] = useState({ name: "", is_active: true, steps: [] });

  const fetchSequences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/drip-sequences`, { withCredentials: true });
      setSequences(res.data);
    } catch (e) {
      // Fetch failed silently
    }
  }, []);

  useEffect(() => { fetchSequences(); }, [fetchSequences]);

  const openDialog = (seq = null) => {
    if (seq) {
      setEditingSeq(seq);
      setFormData({ name: seq.name, is_active: seq.is_active, steps: seq.steps || [] });
    } else {
      setEditingSeq(null);
      setFormData({ name: "", is_active: true, steps: [] });
    }
    setIsDialogOpen(true);
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, { delay_days: formData.steps.length + 1, channel: "email", subject: "", body: "" }],
    });
  };

  const updateStep = (index, field, value) => {
    const updated = [...formData.steps];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, steps: updated });
  };

  const removeStep = (index) => {
    setFormData({ ...formData, steps: formData.steps.filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Sequence name is required");
      return;
    }
    try {
      if (editingSeq) {
        await axios.put(`${API}/drip-sequences/${editingSeq.sequence_id}`, formData, { withCredentials: true });
        toast.success("Sequence updated");
      } else {
        await axios.post(`${API}/drip-sequences`, formData, { withCredentials: true });
        toast.success("Sequence created");
      }
      setIsDialogOpen(false);
      fetchSequences();
    } catch (e) {
      toast.error("Failed to save sequence");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this drip sequence?")) return;
    try {
      await axios.delete(`${API}/drip-sequences/${id}`, { withCredentials: true });
      toast.success("Sequence deleted");
      fetchSequences();
    } catch (e) {
      toast.error("Failed to delete sequence");
    }
  };

  const toggleActive = async (seq) => {
    try {
      await axios.put(`${API}/drip-sequences/${seq.sequence_id}`, { is_active: !seq.is_active }, { withCredentials: true });
      fetchSequences();
    } catch (e) {
      toast.error("Failed to update sequence");
    }
  };

  return (
    <div data-testid="drip-sequences">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Drip Sequences</h1>
          <p className="text-cfc-text-muted text-sm mt-1">
            Automated email/SMS sequences sent to new leads over time
          </p>
        </div>
        <Button
          onClick={() => openDialog()}
          className="bg-cfc-accent hover:bg-cfc-accent-hover text-cfc-primary"
          data-testid="add-sequence-btn"
        >
          <Plus size={18} className="mr-2" /> New Sequence
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="border-cfc-accent/30 bg-cfc-accent/5 mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <Zap size={20} className="text-cfc-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm text-cfc-text-muted">
            <p className="font-medium text-cfc-primary mb-1">How drip sequences work</p>
            <p>Each step is sent automatically based on days after a lead signs up. For example, Day 1: Welcome, Day 3: Program details, Day 7: Tryout invite. Sequences run daily at 9:00 AM. Requires SendGrid (email) or Twilio (SMS) to be configured in Notification Settings.</p>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {sequences.map((seq) => (
          <Card key={seq.sequence_id} className="border-cfc-border" data-testid={`sequence-${seq.sequence_id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="font-heading text-lg text-cfc-primary">{seq.name}</CardTitle>
                  <span className={`text-xs px-2 py-0.5 rounded ${seq.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                    {seq.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={seq.is_active} onCheckedChange={() => toggleActive(seq)} />
                  <Button variant="ghost" size="sm" onClick={() => openDialog(seq)}>
                    <Edit size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(seq.sequence_id)} className="text-cfc-destructive">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {(seq.steps || []).map((step, i) => (
                  <div key={step.step_id || `step-${i}`} className="flex items-center gap-2 bg-cfc-cream-alt px-3 py-2 rounded text-sm">
                    <Clock size={14} className="text-cfc-text-muted" />
                    <span className="text-cfc-primary font-medium">Day {step.delay_days}</span>
                    {step.channel === "email" ? <Mail size={14} className="text-blue-500" /> : <Phone size={14} className="text-green-500" />}
                    <span className="text-cfc-text-muted truncate max-w-[120px]">{step.subject || step.body?.slice(0, 30)}</span>
                  </div>
                ))}
                {(!seq.steps || seq.steps.length === 0) && (
                  <span className="text-sm text-cfc-text-muted">No steps configured</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {sequences.length === 0 && (
          <div className="text-center py-12 text-cfc-text-muted">
            <Zap size={32} className="mx-auto mb-3 text-cfc-text-muted/50" />
            <p>No drip sequences yet. Create one to automatically nurture new leads.</p>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sequence-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-cfc-primary">
              {editingSeq ? "Edit Sequence" : "New Drip Sequence"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="seq-name">Sequence Name</Label>
              <Input
                id="seq-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. New Lead Welcome Series"
                data-testid="sequence-name-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="seq-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="seq-active">Active</Label>
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-cfc-primary font-medium">Steps</Label>
                <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
                  <Plus size={14} className="mr-1" /> Add Step
                </Button>
              </div>
              <div className="space-y-4">
                {formData.steps.map((step, i) => (
                  <Card key={`step-${i}-${step.type}`} className="border-cfc-border">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-cfc-primary">Step {i + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeStep(i)} className="text-cfc-destructive">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Days After Signup</Label>
                          <Input
                            type="number"
                            min="1"
                            value={step.delay_days}
                            onChange={(e) => updateStep(i, "delay_days", parseInt(e.target.value) || 1)}
                            data-testid={`step-${i}-delay`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Channel</Label>
                          <Select value={step.channel} onValueChange={(val) => updateStep(i, "channel", val)}>
                            <SelectTrigger data-testid={`step-${i}-channel`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="sms">SMS</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {step.channel === "email" && (
                        <div>
                          <Label className="text-xs">Subject</Label>
                          <Input
                            value={step.subject}
                            onChange={(e) => updateStep(i, "subject", e.target.value)}
                            placeholder="Email subject line..."
                            data-testid={`step-${i}-subject`}
                          />
                        </div>
                      )}
                      <div>
                        <Label className="text-xs">Body</Label>
                        <Textarea
                          value={step.body}
                          onChange={(e) => updateStep(i, "body", e.target.value)}
                          placeholder="Message body... Use {name} for lead's name"
                          rows={3}
                          data-testid={`step-${i}-body`}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSubmit} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-sequence-btn">
              {editingSeq ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DripSequences;
